import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Plus, Trash2, ImagePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCurrency } from "../lib/currency";
import CategoryAutocomplete from "./CategoryAutocomplete";

function newVariant() {
  return { id: crypto.randomUUID(), name: "", purchase_price_eur: 0, sale_price: 0, stock: 0, image_base64: "" };
}

export default function ProductForm({ open, onOpenChange, product, onSaved }) {
  const isEdit = Boolean(product?.id);
  const { rate } = useCurrency();
  const [form, setForm] = useState({
    name: "", category: "", subcategory: "", description: "", image_base64: "",
    variants: [newVariant()],
  });
  const [saving, setSaving] = useState(false);
  const [categoryList, setCategoryList] = useState([]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/categories");
        setCategoryList(data || []);
      } catch { /* silent */ }
    })();
  }, [open]);

  const topCategories = categoryList.filter((c) => !c.parent).map((c) => c.name);
  const subCategories = categoryList
    .filter((c) => c.parent && c.parent.toLowerCase() === (form.category || "").toLowerCase())
    .map((c) => c.name);

  useEffect(() => {
    if (open) {
      if (product) {
        setForm({
          name: product.name || "",
          category: product.category || "",
          subcategory: product.subcategory || "",
          description: product.description || "",
          image_base64: product.image_base64 || "",
          variants: (product.variants && product.variants.length ? product.variants : [newVariant()]).map((v) => ({
            id: v.id,
            name: v.name,
            purchase_price_eur: rate ? (Number(v.purchase_price) || 0) / rate : (Number(v.purchase_price) || 0),
            sale_price: v.sale_price || 0,
            stock: v.stock || 0,
            image_base64: v.image_base64 || "",
          })),
        });
      } else {
        setForm({ name: "", category: "", subcategory: "", description: "", image_base64: "", variants: [newVariant()] });
      }
    }
  }, [open, product, rate]);

  const updateVariant = (idx, patch) => {
    setForm((f) => ({ ...f, variants: f.variants.map((v, i) => i === idx ? { ...v, ...patch } : v) }));
  };

  const handleImage = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Imagem demasiado grande (máx 2MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category.trim()) {
      toast.error("Nome e categoria são obrigatórios"); return;
    }
    if (form.variants.length === 0 || form.variants.some((v) => !v.name.trim())) {
      toast.error("Adiciona pelo menos uma variante com nome"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        variants: form.variants.map((v) => ({
          id: v.id,
          name: v.name.trim(),
          purchase_price: (Number(v.purchase_price_eur) || 0) * (rate || 100),
          sale_price: Number(v.sale_price) || 0,
          stock: Number(v.stock) || 0,
          image_base64: v.image_base64 || "",
        })),
      };
      if (isEdit) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Produto atualizado");
      } else {
        await api.post("/products", payload);
        toast.success("Produto criado");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Editar produto" : "Novo produto"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="product-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <CategoryAutocomplete
                value={form.category}
                onChange={(v) => setForm({ ...form, category: v })}
                options={topCategories}
                placeholder="Ex: Feminino, Masculino"
                testid="product-category-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Subcategoria</Label>
              <CategoryAutocomplete
                value={form.subcategory}
                onChange={(v) => setForm({ ...form, subcategory: v })}
                options={subCategories}
                placeholder="Ex: Perfumes, Cabelo"
                testid="product-subcategory-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Foto principal do produto</Label>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-md cursor-pointer bg-white hover:bg-secondary text-sm" data-testid="product-image-upload-label">
                  <ImagePlus className="w-4 h-4" /> Escolher
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImage(e.target.files?.[0])} data-testid="product-image-input" />
                </label>
                {form.image_base64 && (
                  <img src={form.image_base64} alt="preview" className="w-12 h-12 object-cover rounded" />
                )}
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="product-description-input" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Variantes / Qualidades</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, variants: [...f.variants, newVariant()] }))} data-testid="add-variant-btn">
                <Plus className="w-4 h-4 mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-3">
              {form.variants.map((v, i) => {
                const handleVariantImage = (file) => {
                  if (!file) return;
                  if (file.size > 2 * 1024 * 1024) { toast.error("Máx 2MB"); return; }
                  const r = new FileReader();
                  r.onload = () => updateVariant(i, { image_base64: r.result });
                  r.readAsDataURL(file);
                };
                const cveEquivalent = (Number(v.purchase_price_eur) || 0) * (rate || 100);
                return (
                  <div key={v.id} className="grid grid-cols-12 gap-2 items-end bg-secondary rounded-lg p-3">
                    <div className="col-span-12 sm:col-span-2 flex items-center sm:justify-center">
                      <label className="w-16 h-16 rounded-md border border-dashed border-border bg-white flex items-center justify-center cursor-pointer overflow-hidden">
                        {v.image_base64 ? (
                          <img src={v.image_base64} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <ImagePlus className="w-5 h-5 text-muted-foreground/60" />
                        )}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handleVariantImage(e.target.files?.[0])} data-testid={`variant-image-${i}`} />
                      </label>
                    </div>
                    <div className="col-span-12 sm:col-span-3 space-y-1">
                      <Label className="text-xs">Nome</Label>
                      <Input value={v.name} onChange={(e) => updateVariant(i, { name: e.target.value })} placeholder="Ex: 150ml" data-testid={`variant-name-${i}`} />
                    </div>
                    <div className="col-span-4 sm:col-span-2 space-y-1">
                      <Label className="text-xs">Compra (€)</Label>
                      <Input type="number" step="0.01" value={v.purchase_price_eur} onChange={(e) => updateVariant(i, { purchase_price_eur: e.target.value })} data-testid={`variant-purchase-${i}`} />
                      <div className="text-[10px] text-muted-foreground">≈ {Math.round(cveEquivalent)} CVE</div>
                    </div>
                    <div className="col-span-4 sm:col-span-2 space-y-1">
                      <Label className="text-xs">Venda (CVE)</Label>
                      <Input type="number" step="0.01" value={v.sale_price} onChange={(e) => updateVariant(i, { sale_price: e.target.value })} data-testid={`variant-sale-${i}`} />
                    </div>
                    <div className="col-span-3 sm:col-span-2 space-y-1">
                      <Label className="text-xs">Stock</Label>
                      <Input type="number" value={v.stock} onChange={(e) => updateVariant(i, { stock: e.target.value })} data-testid={`variant-stock-${i}`} />
                    </div>
                    <div className="col-span-1 sm:col-span-1 flex justify-end">
                      {form.variants.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => setForm((f) => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }))} data-testid={`remove-variant-${i}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[hsl(var(--primary))] hover:bg-[#A21F58]" data-testid="save-product-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
